import { SiteReportDraft } from '@epc/contracts';
import { z } from 'zod';
import { type OfflineScope, SiteOfflineError, type SiteOfflineStore } from './store';

/**
 * Один снимок (по содержимому) — один раз в отчёте: повторный выбор того же файла не создаёт дубль (ПР-03).
 * Смотрим только фото, которые остаются в черновике: убранный снимок можно добавить снова.
 */
function assertNotDuplicate(draft: SiteReportDraft, clientPhotoId: string, sha256: string) {
  if (draft.photos.some((p) => p.clientPhotoId !== clientPhotoId && p.sha256 === sha256))
    throw new SiteOfflineError(
      'photo_duplicate',
      'Этот снимок уже есть в отчёте — второй раз он не добавляется',
    );
}

/** Только локальное хранение. Blob должен быть предварительно очищен файловым слоем.
 * Реальная загрузка, quarantine и ACL сюда не входят. */
export async function storePreparedSitePhoto(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  draftInput: SiteReportDraft,
  clientPhotoId: string,
  blob: Blob,
  expectedLocalRevision: number,
  now: number,
) {
  const draft = SiteReportDraft.parse(draftInput);
  const metadata = draft.photos.find((p) => p.clientPhotoId === clientPhotoId);
  if (!metadata || metadata.fileId || metadata.size !== blob.size)
    throw new SiteOfflineError('invalid_photo', 'Фото и его размер не совпадают с черновиком');
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  if (sha256 !== metadata.sha256)
    throw new SiteOfflineError('invalid_photo', 'Контрольная сумма фото не совпадает');
  await store.mutate(scope, clientId, (row) => {
    if (!row) throw new SiteOfflineError('not_found', 'Сначала сохраните черновик');
    if (row.localRevision !== expectedLocalRevision)
      throw new SiteOfflineError('local_conflict', 'Черновик изменён в другой вкладке');
    const previous = row.photos.find((p) => p.clientPhotoId === clientPhotoId);
    if (previous && (previous.sha256 !== sha256 || previous.size !== blob.size || previous.state === 'sent'))
      throw new SiteOfflineError('photo_conflict', 'Для другого файла нужен новый идентификатор');
    assertNotDuplicate(draft, clientPhotoId, sha256);
    if (!previous)
      row.photos.push({
        clientPhotoId,
        blob,
        sha256,
        size: blob.size,
        state: 'pending',
        uploadId: null,
        parts: {},
        fileId: null,
      });
    row.draft = draft;
    row.localRevision += 1;
    row.savedAt = now;
    return { row, result: undefined };
  });
}

const UploadProgress = z.strictObject({
  uploadId: z.uuid(),
  parts: z.record(z.string().regex(/^[1-9]\d{0,5}$/), z.string().min(1).max(1000)),
});

/**
 * Замена содержимого ещё не отправленного фото (пометка на снимке): новый blob и sha256 в черновике.
 * Уже ушедшее на сервер (`fileId` / state=sent) не трогаем — нужен новый снимок.
 */
export async function replacePendingSitePhoto(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  draftInput: SiteReportDraft,
  clientPhotoId: string,
  blob: Blob,
  expectedLocalRevision: number,
  now: number,
) {
  const draft = SiteReportDraft.parse(draftInput);
  const metadata = draft.photos.find((p) => p.clientPhotoId === clientPhotoId);
  if (!metadata || metadata.fileId || metadata.size !== blob.size)
    throw new SiteOfflineError('invalid_photo', 'Фото и его размер не совпадают с черновиком');
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  if (sha256 !== metadata.sha256)
    throw new SiteOfflineError('invalid_photo', 'Контрольная сумма фото не совпадает');
  await store.mutate(scope, clientId, (row) => {
    if (!row) throw new SiteOfflineError('not_found', 'Сначала сохраните черновик');
    if (row.localRevision !== expectedLocalRevision)
      throw new SiteOfflineError('local_conflict', 'Черновик изменён в другой вкладке');
    const previous = row.photos.find((p) => p.clientPhotoId === clientPhotoId);
    if (!previous) throw new SiteOfflineError('not_found', 'Фото не найдено');
    if (previous.state !== 'pending' || previous.fileId || previous.uploadId)
      throw new SiteOfflineError('photo_conflict', 'Пометить можно только снимок до отправки на сервер');
    assertNotDuplicate(draft, clientPhotoId, sha256);
    previous.blob = blob;
    previous.sha256 = sha256;
    previous.size = blob.size;
    previous.parts = {};
    row.draft = draft;
    row.localRevision += 1;
    row.savedAt = now;
    return { row, result: undefined };
  });
}

/** Только подтверждённые сервером части; не результат локальной отправки байтов. */
export async function recordSitePhotoProgress(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  clientPhotoId: string,
  input: z.infer<typeof UploadProgress>,
) {
  const progress = UploadProgress.parse(input);
  await store.mutate(scope, clientId, (row) => {
    const photo = row?.photos.find((p) => p.clientPhotoId === clientPhotoId);
    if (!row || !photo) throw new SiteOfflineError('not_found', 'Фото не найдено');
    if (photo.state !== 'pending' || (photo.uploadId && photo.uploadId !== progress.uploadId))
      throw new SiteOfflineError('photo_conflict', 'Сеанс загрузки изменился');
    for (const [part, etag] of Object.entries(progress.parts)) {
      if (photo.parts[part] && photo.parts[part] !== etag)
        throw new SiteOfflineError('photo_conflict', 'Подтверждение части изменилось');
      photo.parts[part] = etag;
    }
    photo.uploadId = progress.uploadId;
    return { row, result: undefined };
  });
}

const ReadyReceipt = z.strictObject({
  clientPhotoId: z.uuid(),
  uploadId: z.uuid(),
  fileId: z.uuid(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  size: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  state: z.literal('ready'),
});

/** Вызывается адаптером общего files API после проверки server receipt/ACL.
 * Нормализованный результат адаптера, не новый HTTP-контракт загрузки. */
export async function confirmSitePhotoReady(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  input: z.infer<typeof ReadyReceipt>,
  now: number,
) {
  const receipt = ReadyReceipt.parse(input);
  await store.mutate(scope, clientId, (row) => {
    const photo = row?.photos.find((p) => p.clientPhotoId === receipt.clientPhotoId);
    if (!row || !photo) throw new SiteOfflineError('not_found', 'Фото не найдено');
    if (
      photo.uploadId !== receipt.uploadId ||
      photo.sha256 !== receipt.sha256 ||
      photo.size !== receipt.size ||
      (photo.fileId && photo.fileId !== receipt.fileId)
    )
      throw new SiteOfflineError('photo_conflict', 'Подтверждение относится к другой загрузке');
    if (photo.state === 'sent') return { row, result: undefined };
    photo.fileId = receipt.fileId;
    photo.state = 'sent';
    // Не теряем незавершённый ввод и не воскрешаем удалённое фото.
    const draft = row.draft as {
      photos?: Array<{ clientPhotoId: string; fileId: string | null; sha256: string; size: number } | null>;
    } | null;
    const metadata = Array.isArray(draft?.photos)
      ? draft.photos.find((p) => p?.clientPhotoId === receipt.clientPhotoId)
      : undefined;
    if (metadata) {
      if (metadata.sha256 !== receipt.sha256 || metadata.size !== receipt.size)
        throw new SiteOfflineError('photo_conflict', 'Черновик ссылается на другое содержимое фото');
      metadata.fileId = receipt.fileId;
      row.localRevision += 1;
      row.savedAt = now;
    }
    return { row, result: undefined };
  });
}
